
import React from 'react';
import { LayoutDashboard, Projector, Wrench, ShieldAlert, BarChart, Waypoints, ListTree, TestTube2, FileText, FilePen, Sparkles, Bot, Timer, Network, FolderTree, Activity, Shield, Share2, ListChecks, AppWindow, Database, FileEdit, Users, GitBranch, Building2, Microscope, Package, BookOpen } from 'lucide-react';
import { NavLinkItem } from './types';
import { getPfmeaAppUrl, getResolveAppUrl } from './config/appUrls';

// Get PFMEA app URL (environment-aware)
const PFMEA_URL = getPfmeaAppUrl();
const RESOLVE_URL = getResolveAppUrl();

export const NAV_LINKS: NavLinkItem[] = [
    // === OVERVIEW ===
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} />, advanced: false },
    { name: 'Learning Center', path: '/learning-center', icon: <BookOpen size={20} />, advanced: false },
    { name: 'AI Assistant', path: '/ai-assistant', icon: <Bot size={20} />, advanced: false },

    // === 1. PLANNING & REQUIREMENTS ===
    {
        name: '1. Planning',
        path: '/planning',
        icon: <ListChecks size={20} />,
        advanced: false,
        children: [
            { name: 'Project Management', path: '/projects', icon: <FolderTree size={20} /> },
            { name: 'Products', path: '/products', icon: <Package size={20} /> },
            { name: 'Customers', path: '/customers', icon: <Building2 size={20} /> },
            { name: 'Customer Requirements', path: '/customer-requirements', icon: <ListChecks size={20} /> },
            { name: 'Workflow Gates', path: '/workflow-gates', icon: <Shield size={20} /> },
            { name: 'Action Tracking', path: '/action-tracking', icon: <Activity size={20} /> },
            { name: 'Epoch Manager', path: '/epoch-manager', icon: <Timer size={20} /> },
            { name: 'Method Comparison', path: '/methodology-comparison', icon: <FileText size={20} /> },
            { name: 'ECN Management', path: '/ecn-management', icon: <FileEdit size={20} /> },
        ]
    },



    // === 2. DESIGN & STRUCTURE ===
    {
        name: '2. Design Structure',
        path: '/design',
        icon: <FolderTree size={20} />,
        advanced: false,
        children: [
            { name: 'Structure Analysis (Step 2)', path: '/structure-tree', icon: <Network size={20} /> },
            { name: 'Function Network (Step 3)', path: '/function-net', icon: <Projector size={20} /> },
            { name: 'Failure Network (Step 4)', path: '/failure-net', icon: <Share2 size={20} /> },
            { name: 'Product Structure (PBS)', path: '/pbs-manager', icon: <Network size={20} /> },
            { name: 'BOM Functions', path: '/bom-functions', icon: <ListTree size={20} /> },
            { name: 'System Definition', path: '/system-definition', icon: <Wrench size={20} /> },
            { name: 'Materials Database', path: '/materials-database', icon: <Database size={20} /> },
        ]
    },

    // === 3. P-DIAGRAM ===
    {
        name: '3. P-Diagram',
        path: '/p-diagram',
        icon: <Projector size={20} />,
        advanced: false,
        children: [
            { name: 'View P-Diagrams', path: '/p-diagram', icon: <Projector size={20} /> },
        ]
    },

    // === 4. MAGNUS PRIME (AI-POWERED DFR) ===
    {
        name: '4. Magnus Prime',
        path: '/magnus-prime',
        icon: <Sparkles size={20} />,
        advanced: false,
        children: [
            { name: 'Magnus Genesis', path: '/magnus-prime', icon: <Sparkles size={20} /> },
            { name: 'Magnus Insight', path: '/visualization/tree', icon: <GitBranch size={20} /> },
            { name: 'Advanced Convergence', path: '/advanced-convergence', icon: <Network size={20} /> },
        ]
    },

    // === 5. DFMEA PORTAL ===
    {
        name: 'DFMEA Portal',
        path: '/dfmea',
        icon: <ShieldAlert size={20} />,
        advanced: false,
        children: [
            { name: 'FMEA Table', path: '/fmeas', icon: <FileText size={20} /> },
            { name: 'FMEA Editor', path: '/fmea-editor', icon: <FilePen size={20} /> },
            { name: 'AI Ideation', path: '/auto-fmea', icon: <Sparkles size={20} /> },
            { name: 'Risk Matrix', path: '/risk-analysis', icon: <ShieldAlert size={20} /> },
            { name: 'Controls Library', path: '/controls-library', icon: <Shield size={20} /> },
        ]
    },

    // === PFMEA PORTAL (Process FMEA - Separate App) ===
    {
        name: 'PFMEA Portal',
        path: '/pfmea',
        icon: <Shield size={20} />,
        advanced: false,
        children: [
            { name: 'PFMEA Dashboard', path: `${PFMEA_URL}/`, icon: <LayoutDashboard size={20} />, external: true },
            { name: 'Process Flow', path: `${PFMEA_URL}/process-flow`, icon: <Waypoints size={20} />, external: true },
            { name: 'PFMEA Table', path: `${PFMEA_URL}/pfmea-table`, icon: <FileText size={20} />, external: true },
            { name: 'PFMEA Editor', path: `${PFMEA_URL}/pfmea-editor`, icon: <FilePen size={20} />, external: true },
            { name: 'Control Plan', path: `${PFMEA_URL}/control-plan`, icon: <ListChecks size={20} />, external: true },
            { name: 'Work Instructions', path: `${PFMEA_URL}/work-instructions`, icon: <FileEdit size={20} />, external: true },
            { name: 'AI Smart Suggest', path: `${PFMEA_URL}/ideation`, icon: <Sparkles size={20} />, external: true },
        ]
    },

    // === MAGNUS RESOLVE PORTAL (Failure Analysis - Separate App) ===
    {
        name: 'Magnus Resolve',
        path: '/resolve',
        icon: <Activity size={20} />,
        advanced: false,
        children: [
            { name: 'Resolve Dashboard', path: `${RESOLVE_URL}/`, icon: <LayoutDashboard size={20} />, external: true },
            { name: 'AI Assistant', path: `${RESOLVE_URL}/ai-assistant`, icon: <Bot size={20} />, external: true },
            { name: 'Reports', path: `${RESOLVE_URL}/reports/advanced`, icon: <FileText size={20} />, external: true },
        ]
    },

    // === 6. TRACEABILITY & LINKS ===
    {
        name: '6. Traceability',
        path: '/traceability',
        icon: <Share2 size={20} />,
        advanced: false,
        children: [
            { name: 'Traceability Matrix', path: '/traceability-matrix', icon: <BarChart size={20} /> },
            { name: 'Link Explorer', path: '/link-explorer', icon: <Share2 size={20} /> },
            { name: 'Graph View', path: '/graph-view', icon: <Waypoints size={20} /> },
        ]
    },

    // === 7. VALIDATION & TESTING ===
    {
        name: '7. Validation',
        path: '/validation',
        icon: <TestTube2 size={20} />,
        advanced: false,
        children: [
            { name: 'LIMS (Validation Lab)', path: '/lims', icon: <Microscope size={20} /> },
            { name: 'Reliability Test Plan', path: '/reliability-plan', icon: <TestTube2 size={20} /> },
            { name: 'Physics Lab', path: '/physics-verification', icon: <TestTube2 size={20} /> },
            { name: 'Reliability Suite', path: '/external-app', icon: <AppWindow size={20} /> },
        ]
    },

    // === DATA MANAGEMENT ===
    {
        name: 'Data & Materials',
        path: '/data',
        icon: <Database size={20} />,
        advanced: false,
        children: [
            { name: 'Local Materials', path: '/materials-database', icon: <Database size={20} /> },
            { name: 'Component Search', path: '/component-search', icon: <Activity size={20} /> },
        ]
    },

    // === AUTOGAIN ACCESS PORTAL (AutoGain Staff Only) ===
    // AutoGain admins manage the platform itself
    {
        name: 'AutoGain Portal',
        path: '/platform',
        icon: <Building2 size={20} />,
        advanced: true,
        requiredRole: 'autoGainAdmin', // Only AutoGain staff see this
        children: [
            // AutoGain-specific platform management tools
            { name: 'Security Command', path: '/platform', icon: <Shield size={20} /> },
            { name: 'Client Companies', path: '/platform/clients', icon: <Building2 size={20} /> },
            { name: 'Platform Users', path: '/admin/users', icon: <Users size={20} /> },
            { name: 'Subscription Plans', path: '/platform/subscriptions', icon: <Shield size={20} /> },
            { name: 'Platform Analytics', path: '/platform/analytics', icon: <BarChart size={20} /> },
            { name: 'AI Config', path: '/admin/bots', icon: <Bot size={20} /> },
            { name: 'Troubleshooting', path: '/troubleshooting', icon: <Activity size={20} /> },
            { name: 'Debug Mode', path: '/platform/debug', icon: <Activity size={20} /> },
        ]
    },

    // === CUSTOMER ACCESS PORTAL (Client Admins Only) ===
    // Customer admins manage their own organization
    {
        name: 'Customer Portal',
        path: '/admin',
        icon: <Shield size={20} />,
        advanced: true,
        requiredRole: 'clientAdmin', // Only client admins see this
        children: [
            { name: 'Security Overview', path: '/admin/security', icon: <Activity size={20} /> },
            { name: 'Team Management', path: '/admin/users', icon: <Users size={20} /> },
            { name: 'User Permissions', path: '/admin/permissions', icon: <Shield size={20} /> },
            { name: 'User Analytics', path: '/admin/analytics', icon: <BarChart size={20} /> },
        ]
    },
];
